import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ShoppingCart, ArrowLeft, Zap } from "lucide-react";
import { api, euro, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Stars } from "@/components/site/Stars";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => { api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return <div className="max-w-7xl mx-auto px-5 py-24 text-zinc-400" data-testid="product-not-found">Servizio non trovato. <Link to="/servizi" className="text-red-500">Torna ai servizi</Link></div>;
  if (!p) return <div className="min-h-[60vh] grid place-items-center text-zinc-500">Caricamento…</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12" data-testid="product-detail-page">
      <Link to="/servizi" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8" data-testid="product-back-link"><ArrowLeft size={16} /> Servizi & prodotti</Link>
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="relative reveal">
          <div className="absolute -top-3 -left-3 w-24 h-24 border-t-4 border-l-4 border-red-600" />
          <img src={imgUrl(p.image_url)} alt={p.name} className="w-full aspect-[4/3] object-cover" data-testid="product-image" />
        </div>
        <div className="reveal" style={{ animationDelay: "100ms" }}>
          <span className="eyebrow">{p.category}</span>
          <h1 className="tms-title text-5xl sm:text-6xl mt-3" data-testid="product-name">{p.name}</h1>
          <div className="flex items-center gap-3 mt-4 text-sm text-zinc-400">
            <Stars value={p.rating_avg} size={18} testid="product-rating" />
            <span>{p.rating_count ? `${p.rating_avg} su 5 · ${p.rating_count} recensioni` : "Nessuna recensione"}</span>
          </div>
          <p className="font-display italic font-black text-5xl text-red-500 mt-8" data-testid="product-price">{euro(p.price)}</p>
          <p className="text-xs text-zinc-500 mt-1">IVA inclusa</p>
          <p className="text-zinc-300 leading-relaxed mt-8 whitespace-pre-line" data-testid="product-description">{p.description || p.short_description}</p>
          <div className="flex items-center gap-4 mt-10">
            <div className="flex items-center border border-zinc-700">
              <button className="p-3 hover:text-red-500" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="product-qty-decrease"><Minus size={16} /></button>
              <span className="w-10 text-center font-bold" data-testid="product-qty">{qty}</span>
              <button className="p-3 hover:text-red-500" onClick={() => setQty(Math.min(20, qty + 1))} data-testid="product-qty-increase"><Plus size={16} /></button>
            </div>
            <button className="btn-red flex-1" onClick={() => add(p, qty)} data-testid="product-buy-button"><Zap size={18} /> Acquista</button>
          </div>
          <button className="mt-4 flex items-center gap-2 text-sm text-zinc-400 hover:text-white" onClick={() => add(p, qty)} data-testid="product-add-cart-button">
            <ShoppingCart size={16} /> Aggiungi al carrello e continua
          </button>
        </div>
      </div>

      <section className="mt-24" data-testid="product-reviews">
        <p className="eyebrow mb-3">Recensioni verificate</p>
        <h2 className="tms-title text-4xl mb-8">Chi l'ha <span className="font-script normal-case text-red-500">provato</span></h2>
        {p.reviews.length === 0 ? (
          <p className="text-zinc-500" data-testid="product-no-reviews">Nessuna recensione approvata per questo articolo.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {p.reviews.map((r) => (
              <article key={r.id} className="bg-[#18181C] border border-zinc-800 p-6" data-testid={`product-review-${r.id}`}>
                <div className="flex justify-between items-center">
                  <span className="font-display italic uppercase font-bold">{r.name}</span>
                  <Stars value={r.rating} />
                </div>
                <p className="text-zinc-300 mt-3">{r.comment}</p>
                <p className="text-xs text-zinc-600 mt-3">{new Date(r.created_at).toLocaleDateString("it-IT")}</p>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
